/**
 * Browser-First AI SDK Client
 * WebLLM-only implementation for browser-based LLM inference
 *
 * Step 2: Added engine adapter support (WebLLM)
 * Step 3: Added manifest-driven model configuration
 * Step 4: Added ModelStore for status tracking and progress callbacks
 * Step 5: Added AsyncIterable streaming with ChatDeltaEvent
 * Step 6: Added benchmark infrastructure
 * Step 7: Added health() and SDKError
 * V1.1: Added device detection and smart model routing
 * V1.2: Added signed manifest verification
 * V1.4: Added smart preloading with best-fit model selection
 */
import { runBenchmarks } from './bench/runner.js';
import { createLogger } from './utils/logger.js';
import { WebLLMAdapter } from './engines/webllm/WebLLMAdapter.js';
import { loadManifestWithVerification, findModel, } from './models/manifest.js';
import { createStoreRecord } from './storage/ModelStore.js';
import { createIndexedDBModelStore } from './storage/IndexedDBModelStore.js';
import { SDKError, wrapError } from './errors.js';
import { checkWebGPU, } from './health.js';
import { detectDevice } from './device/detect.js';
import { selectModel, getSmallerAlternatives } from './router/selectModel.js';
import { startPreload, shouldAttemptPreload } from './preload/preloadManager.js';
/**
 * Internal client implementation
 * WebLLM-only - requires engine configuration
 */
class BrowserFirstClient {
    constructor(config) {
        this.adapterInitialized = false;
        this.initPromise = null;
        this.manifest = null;
        this.manifestPromise = null;
        this.lastWarmStart = false; // Track warm start status for streaming
        this.requestCounter = 0; // For generating unique request IDs
        this.preloadPromise = null; // V1.4: Preload tracking
        // Require engine configuration - stub mode removed
        if (!config.engine) {
            throw new SDKError('CONFIG_ERROR', 'ClientConfig.engine is required (webllm) — stub mode removed in this repo. ' +
                'Use: createClient({ engine: "webllm", manifestUrl: "/models.json" })');
        }
        if (config.engine !== 'webllm') {
            throw new SDKError('CONFIG_ERROR', `Unsupported engine "${config.engine}". Only "webllm" is supported.`);
        }
        if (!config.manifestUrl) {
            throw new SDKError('CONFIG_ERROR', 'ClientConfig.manifestUrl is required when engine is set to "webllm"');
        }
        this.config = config;
        this.logger = createLogger(config.logLevel ?? 'info');
        this.onProgress = config.onProgress;
        this.logger.info('Client initialized', {
            engine: config.engine,
            manifestUrl: config.manifestUrl,
            defaultModel: config.defaultModel,
        });
        // Initialize model store (use provided or create default)
        this.modelStore = config.modelStore ?? createIndexedDBModelStore({
            dbName: config.storageKeyPrefix ?? 'browser-first-ai',
        });
        // Create WebLLM adapter
        this.adapter = new WebLLMAdapter(this.logger, {
            onProgress: this.onProgress,
        });
        // V1.4: Start preloading if enabled
        if (shouldAttemptPreload(config.preload)) {
            this.startPreloading();
        }
    }
    /**
     * Start preloading in the background (V1.4).
     * Non-blocking - runs independently of client creation.
     */
    startPreloading() {
        // Don't await - run in background
        this.preloadPromise = this.doPreload();
        // Log result but don't block
        this.preloadPromise
            .then((result) => {
            if (result.preloaded) {
                this.logger.info('Preload complete', { modelId: result.modelId });
            }
            else if (result.fromCache) {
                this.logger.debug('Model already cached', { modelId: result.modelId, reason: result.reason });
            }
            else {
                this.logger.debug('Preload skipped', { reason: result.reason });
            }
        })
            .catch((err) => {
            this.logger.warn('Preload failed', { error: err instanceof Error ? err.message : String(err) });
        });
    }
    /**
     * Perform the actual preload operation (V1.4).
     */
    async doPreload() {
        // Wait for manifest to load first
        const manifest = await this.ensureManifestLoaded();
        // Convert manifest entries to ModelInfo
        const models = manifest.models.map((entry) => ({
            id: entry.id,
            displayName: entry.displayName,
            engine: entry.engine,
            sizeMB: entry.sizeMB,
            contextLength: entry.contextLength,
            quantization: entry.quantization,
            description: entry.description,
            tier: entry.tier,
            tags: entry.tags,
            minRecommendedMemoryGB: entry.minRecommendedMemoryGB,
        }));
        return startPreload({
            models,
            modelStore: this.modelStore,
            config: this.config.preload,
            loadModelFn: async (modelId) => {
                await this.ensureModelLoaded(modelId);
            },
            onSDKProgress: this.onProgress,
        });
    }
    /**
     * Wait for preload to complete (V1.4).
     * Returns immediately if preloading is disabled or already complete.
     */
    async waitForPreload() {
        return this.preloadPromise;
    }
    /**
     * Load and cache the model manifest.
     * Returns cached manifest if already loaded.
     * V1.2: Supports signature verification via registry config.
     */
    async ensureManifestLoaded() {
        if (this.manifest) {
            return this.manifest;
        }
        if (!this.config.manifestUrl) {
            throw new SDKError('CONFIG_ERROR', 'No manifestUrl configured');
        }
        // Use a single promise to prevent concurrent manifest loads
        if (!this.manifestPromise) {
            this.logger.debug('Loading manifest', {
                url: this.config.manifestUrl,
                requireSignature: this.config.registry?.requireSignature,
            });
            this.manifestPromise = loadManifestWithVerification(this.config.manifestUrl, this.config.registry)
                .then((result) => {
                this.manifest = result.manifest;
                this.manifestVerification = result.verification;
                // Log verification status
                if (result.verification) {
                    if (result.verification.verified) {
                        this.logger.info('Manifest loaded and verified', {
                            version: result.manifest.version,
                            modelCount: result.manifest.models.length,
                            publicKey: result.verification.publicKey?.slice(0, 16) + '...',
                        });
                    }
                    else if (result.verification.skipped) {
                        this.logger.debug('Manifest loaded (verification skipped)', {
                            version: result.manifest.version,
                            modelCount: result.manifest.models.length,
                        });
                    }
                    else {
                        this.logger.warn('Manifest loaded but verification failed', {
                            version: result.manifest.version,
                            error: result.verification.error,
                        });
                    }
                }
                else {
                    this.logger.info('Manifest loaded', {
                        version: result.manifest.version,
                        modelCount: result.manifest.models.length,
                    });
                }
                return result.manifest;
            })
                .catch((error) => {
                // Check if this is a signature verification error
                const errorMsg = error instanceof Error ? error.message : String(error);
                if (errorMsg.includes('signature verification failed')) {
                    throw new SDKError('MANIFEST_SIGNATURE_INVALID', errorMsg, error);
                }
                throw wrapError('MANIFEST_LOAD_ERROR', error, `Failed to load manifest from ${this.config.manifestUrl}`);
            });
        }
        return this.manifestPromise;
    }
    /**
     * Ensure the adapter is initialized.
     * Lazy initialization on first use.
     */
    async ensureInitialized() {
        if (this.adapterInitialized)
            return;
        // Use a single init promise to prevent concurrent initialization
        if (!this.initPromise) {
            this.initPromise = this.adapter.init().then(() => {
                this.adapterInitialized = true;
            });
        }
        await this.initPromise;
    }
    /**
     * Resolve the model ID from request.
     * Handles 'auto' with smart device-aware routing (V1.1).
     */
    async resolveModelId(requestModel) {
        const manifest = await this.ensureManifestLoaded();
        // Handle 'auto' model selection with smart routing (V1.1)
        if (requestModel === 'auto' || !requestModel) {
            // If defaultModel is set, treat it as an explicit request
            if (this.config.defaultModel && requestModel !== 'auto') {
                const model = findModel(manifest, this.config.defaultModel);
                if (!model) {
                    throw new SDKError('MODEL_NOT_FOUND', `Default model "${this.config.defaultModel}" not found in manifest. ` +
                        `Available models: ${manifest.models.map((m) => m.id).join(', ')}`);
                }
                this.logger.debug('Using default model', { modelId: this.config.defaultModel });
                return this.config.defaultModel;
            }
            // Smart routing: detect device and select best model
            try {
                const device = await detectDevice();
                const routingConfig = this.config.routing ?? {};
                // Convert manifest entries to ModelInfo for router
                const models = manifest.models.map((entry) => ({
                    id: entry.id,
                    displayName: entry.displayName,
                    engine: entry.engine,
                    sizeMB: entry.sizeMB,
                    contextLength: entry.contextLength,
                    quantization: entry.quantization,
                    description: entry.description,
                    tier: entry.tier,
                    tags: entry.tags,
                    minRecommendedMemoryGB: entry.minRecommendedMemoryGB,
                }));
                const selection = selectModel({
                    device,
                    models,
                    requestedModel: 'auto',
                    engine: this.config.engine ?? 'webllm',
                    preference: {
                        tier: routingConfig.defaultTier,
                        tag: routingConfig.defaultTag,
                    },
                });
                // Log selection reasons and warnings
                this.logger.info('Smart model selection', {
                    selectedModel: selection.selected.id,
                    reasons: selection.reasons,
                    warnings: selection.warnings,
                    candidateCount: selection.candidates.length,
                });
                if (selection.warnings && selection.warnings.length > 0) {
                    for (const warning of selection.warnings) {
                        this.logger.warn('Model selection warning', { warning });
                    }
                }
                return selection.selected.id;
            }
            catch (routerError) {
                // Fall back to first model if routing fails
                this.logger.warn('Smart routing failed, falling back to first model', { error: routerError });
                if (manifest.models.length > 0) {
                    return manifest.models[0].id;
                }
                throw new SDKError('MODEL_NOT_FOUND', 'No models available in manifest');
            }
        }
        // Validate requested model exists in manifest
        const model = findModel(manifest, requestModel);
        if (!model) {
            throw new SDKError('MODEL_NOT_FOUND', `Model "${requestModel}" not found in manifest. ` +
                `Available models: ${manifest.models.map((m) => m.id).join(', ')}`);
        }
        return requestModel;
    }
    /**
     * Check if model is warm (previously loaded successfully)
     */
    async checkWarmStart(modelId) {
        try {
            const manifest = await this.ensureManifestLoaded();
            const record = await this.modelStore.get(modelId);
            if (!record)
                return false;
            // Check if status is 'ready' and manifest version matches
            if (record.status === 'ready') {
                // If manifest version matches, it's a warm start
                if (record.manifestVersion === manifest.version) {
                    this.logger.debug('Warm start detected', { modelId, manifestVersion: manifest.version });
                    return true;
                }
                // Manifest version changed - invalidate
                this.logger.debug('Manifest version changed, invalidating warm start', {
                    modelId,
                    oldVersion: record.manifestVersion,
                    newVersion: manifest.version,
                });
            }
            return false;
        }
        catch (error) {
            this.logger.warn('Failed to check warm start status', { modelId, error });
            return false;
        }
    }
    /**
     * Update model status in store
     */
    async updateModelStatus(modelId, status, lastError) {
        try {
            const manifest = await this.ensureManifestLoaded();
            const record = createStoreRecord(modelId, 'webllm', status, manifest.version, lastError);
            await this.modelStore.set(modelId, record);
            this.logger.debug('Model status updated', { modelId, status });
        }
        catch (error) {
            this.logger.warn('Failed to update model status', { modelId, status, error });
        }
    }
    /**
     * Generate a unique request ID
     */
    generateRequestId() {
        this.requestCounter++;
        return `req_${Date.now()}_${this.requestCounter}`;
    }
    /**
     * Ensure a model is loaded.
     * Resolves the model ID and loads it via the adapter.
     * Also tracks warm start status and load time for streaming/benchmarking.
     */
    async ensureModelLoaded(requestModel) {
        await this.ensureInitialized();
        const modelId = await this.resolveModelId(requestModel);
        // Check for warm start
        const warmStart = await this.checkWarmStart(modelId);
        this.lastWarmStart = warmStart; // Track for streaming metadata
        // Update status to 'loading'
        await this.updateModelStatus(modelId, 'loading');
        // Track load time for benchmarking
        this.loadStartTime = performance.now();
        this.lastLoadTimeMs = undefined; // Reset before load
        try {
            // Load model with warm start hint
            await this.adapter.loadModel(modelId, warmStart);
            // Calculate load time
            this.lastLoadTimeMs = performance.now() - this.loadStartTime;
            this.logger.debug('Model load time', { modelId, loadTimeMs: this.lastLoadTimeMs });
            // Update status to 'ready'
            await this.updateModelStatus(modelId, 'ready');
            return modelId;
        }
        catch (error) {
            // Update status to 'error'
            this.lastLoadTimeMs = undefined;
            const errorMsg = error instanceof Error ? error.message : String(error);
            await this.updateModelStatus(modelId, 'error', errorMsg);
            // Find smaller alternatives for graceful degradation (V1.1)
            const manifest = await this.ensureManifestLoaded();
            const models = manifest.models.map((entry) => ({
                id: entry.id,
                displayName: entry.displayName,
                engine: entry.engine,
                sizeMB: entry.sizeMB,
                contextLength: entry.contextLength,
                quantization: entry.quantization,
                description: entry.description,
                tier: entry.tier,
                tags: entry.tags,
                minRecommendedMemoryGB: entry.minRecommendedMemoryGB,
            }));
            const alternatives = getSmallerAlternatives(modelId, models, 3);
            const tryModelIds = alternatives.map((m) => m.id);
            if (tryModelIds.length > 0) {
                this.logger.info('Model load failed, suggesting alternatives', {
                    failedModel: modelId,
                    alternatives: tryModelIds,
                });
            }
            throw new SDKError('MODEL_LOAD_ERROR', `Failed to load model "${modelId}": ${errorMsg}`, error, { tryModelIds: tryModelIds.length > 0 ? tryModelIds : undefined, failedModelId: modelId });
        }
    }
    /**
     * Get the last recorded model load time (for benchmarking).
     * Returns undefined if no load occurred or load failed.
     */
    getLastLoadTimeMs() {
        const loadTime = this.lastLoadTimeMs;
        // Clear after reading to avoid double-counting
        this.lastLoadTimeMs = undefined;
        return loadTime;
    }
    /**
     * List available models from the manifest.
     */
    async listModels() {
        const manifest = await this.ensureManifestLoaded();
        return manifest.models.map((entry) => ({
            id: entry.id,
            displayName: entry.displayName,
            engine: entry.engine,
            sizeMB: entry.sizeMB,
            contextLength: entry.contextLength,
            quantization: entry.quantization,
            description: entry.description,
            tier: entry.tier,
            tags: entry.tags,
            minRecommendedMemoryGB: entry.minRecommendedMemoryGB,
        }));
    }
    /**
     * Get a health report for the SDK and its dependencies.
     * Never throws - returns a HealthReport even on failure.
     */
    async health(modelId) {
        const notes = [];
        let overallStatus = 'ok';
        // Check WebGPU
        const webgpu = await checkWebGPU();
        if (!webgpu.supported) {
            overallStatus = 'error';
            notes.push('WebGPU is not available. Use Chrome 113+, Edge 113+, or another WebGPU-enabled browser.');
        }
        // Check manifest
        const manifest = { loaded: false };
        try {
            const m = await this.ensureManifestLoaded();
            manifest.loaded = true;
            manifest.url = this.config.manifestUrl;
            manifest.version = m.version;
            manifest.modelCount = m.models.length;
            if (m.models.length === 0) {
                overallStatus = overallStatus === 'error' ? 'error' : 'degraded';
                notes.push('Manifest loaded but contains no models.');
            }
        }
        catch (error) {
            manifest.loaded = false;
            manifest.url = this.config.manifestUrl;
            manifest.error = error instanceof Error ? error.message : String(error);
            overallStatus = 'error';
            notes.push(`Failed to load manifest: ${manifest.error}`);
        }
        // Check model status (if modelId provided or manifest has models)
        let model;
        const targetModelId = modelId || (manifest.loaded && manifest.modelCount && manifest.modelCount > 0
            ? this.manifest?.models[0]?.id
            : undefined);
        if (targetModelId) {
            model = { selectedModelId: targetModelId };
            try {
                const record = await this.modelStore.get(targetModelId);
                if (record) {
                    model.storeStatus = record.status;
                    model.warmStart = record.status === 'ready';
                    if (record.lastError) {
                        model.error = record.lastError;
                    }
                }
                else {
                    model.storeStatus = 'unknown';
                    model.warmStart = false;
                    notes.push(`Model "${targetModelId}" has not been loaded before (cold start expected).`);
                }
            }
            catch (error) {
                model.storeStatus = 'unknown';
                model.error = error instanceof Error ? error.message : String(error);
            }
        }
        // Detect device capabilities (V1.1)
        let device;
        try {
            device = await detectDevice();
            // Add device-specific notes
            if (device.inferenceHints.notes.length > 0) {
                notes.push(...device.inferenceHints.notes.slice(0, 2)); // Add first 2 hints
            }
        }
        catch (error) {
            this.logger.warn('Failed to detect device capabilities', { error });
        }
        // Add helpful notes
        if (overallStatus === 'ok' && notes.length === 0) {
            notes.push('All systems operational.');
        }
        return {
            status: overallStatus,
            engine: this.config.engine,
            webgpu,
            manifest,
            model,
            device,
            notes,
        };
    }
    /**
     * Send a chat request and receive a response
     */
    async chat(request) {
        this.validateRequest(request);
        const modelId = await this.ensureModelLoaded(request.model);
        this.logger.debug('Starting chat', { model: modelId });
        return this.adapter.chat({ ...request, model: modelId });
    }
    /**
     * Send a chat request with streaming (Step 5).
     * Returns an AsyncIterable of ChatDeltaEvent objects.
     */
    chatStream(request) {
        const self = this;
        const requestId = this.generateRequestId();
        async function* generateStream() {
            try {
                self.validateRequest(request);
            }
            catch (err) {
                yield {
                    type: 'error',
                    modelId: request.model || 'unknown',
                    requestId,
                    ts: Date.now(),
                    error: { message: err instanceof Error ? err.message : String(err), code: 'VALIDATION_ERROR' },
                };
                return;
            }
            // Check for abort before starting
            if (request.signal?.aborted) {
                yield {
                    type: 'error',
                    modelId: request.model || 'unknown',
                    requestId,
                    ts: Date.now(),
                    error: { message: 'Request aborted before starting', code: 'ABORTED' },
                };
                return;
            }
            // Engine-backed streaming inference
            try {
                const modelId = await self.ensureModelLoaded(request.model);
                self.logger.debug('Starting streaming chat', { model: modelId, requestId });
                // Delegate to adapter's streaming implementation
                yield* self.adapter.chatStream({ ...request, model: modelId }, requestId, self.lastWarmStart);
            }
            catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                yield {
                    type: 'error',
                    modelId: request.model,
                    requestId,
                    ts: Date.now(),
                    error: { message, code: 'LOAD_ERROR' },
                };
            }
        }
        return generateStream();
    }
    /**
     * Send a chat request with callback-based streaming.
     * @deprecated Use chatStream() returning AsyncIterable instead.
     */
    async chatStreamCallback(request, onToken) {
        this.validateRequest(request);
        const modelId = await this.ensureModelLoaded(request.model);
        this.logger.debug('Starting streaming chat (callback - deprecated)', { model: modelId });
        return this.adapter.chatStreamCallback({ ...request, model: modelId }, onToken);
    }
    /**
     * Run performance benchmarks on the model.
     * Measures TTFT (time to first token), tokens/sec, and total generation time.
     */
    async benchmark(cases, options, onProgress) {
        this.logger.info('Starting benchmark', {
            caseCount: cases.length,
            model: options.model,
            runs: options.runs ?? 3,
            warmupRuns: options.warmupRuns ?? 1,
        });
        // Pass getLastLoadTimeMs as a callback for the runner to capture load times
        const results = await runBenchmarks(this, cases, options, onProgress, () => this.getLastLoadTimeMs());
        this.logger.info('Benchmark completed', {
            caseCount: results.length,
            totalRuns: results.reduce((sum, r) => sum + r.runs.length, 0),
        });
        return results;
    }
    /**
     * Validate chat request
     */
    validateRequest(request) {
        if (!request.messages || request.messages.length === 0) {
            this.logger.error('Missing messages in chat request');
            throw new Error('ChatRequest.messages must contain at least one message');
        }
    }
}
/**
 * Create a new Browser-First AI client
 * @param config - Client configuration options (engine and manifestUrl required)
 * @returns A configured client instance
 */
export function createClient(config) {
    return new BrowserFirstClient(config);
}
//# sourceMappingURL=client.js.map